import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Radar, Inbox, Send } from 'lucide-react';
import api from '@/lib/api';

const fmtValue = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function dueTone(daysLeft) {
  if (daysLeft <= 3) return 'bg-red-500/15 text-red-400 border-red-500/25';
  if (daysLeft <= 7) return 'bg-amber-500/15 text-amber-400 border-amber-500/25';
  return 'bg-blue-500/15 text-blue-400 border-blue-500/25';
}

function WidgetCard({ icon: Icon, title, count, children, testId }) {
  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid={testId}>
      <CardHeader className="pb-2 border-b border-white/[0.08]">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-primary shrink-0" /> {title}
          <span className="text-xs text-muted-foreground/70 ml-auto tabular-nums">{count}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-2 space-y-0">{children}</CardContent>
    </Card>
  );
}

function OutreachQueueCard({ items, navigate }) {
  return (
    <WidgetCard icon={Send} title="My Outreach Queue" count={items.length} testId="outreach-queue-card">
      {items.map(l => (
        <button
          key={l.id}
          type="button"
          className="w-full text-left py-2 border-b border-white/[0.06] last:border-0 hover:bg-secondary/20 transition-colors rounded-sm"
          onClick={() => navigate(`/leads?lead=${l.id}`)}
          data-testid={`outreach-lead-${l.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-normal truncate flex-1">{l.company || l.contact_name}</p>
            <Badge variant="outline" className="text-xs shrink-0">{l.pipeline_stage}</Badge>
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {l.next_step ? `Next: ${l.next_step}` : 'No next step set'}
          </p>
        </button>
      ))}
    </WidgetCard>
  );
}

function RfpRadarCard({ items, navigate }) {
  return (
    <WidgetCard icon={Radar} title="RFP Deadline Radar" count={items.length} testId="rfp-radar-card">
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No RFP or proposal deadlines in the next 14 days</p>
      ) : (
        items.map(i => (
          <button
            key={`${i.type}-${i.id}`}
            type="button"
            className="w-full text-left flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2 hover:bg-secondary/20 transition-colors rounded-sm"
            onClick={() => navigate(i.type === 'rfp' ? '/leads' : '/proposals')}
            data-testid={`radar-item-${i.id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-normal truncate">{i.title}</p>
              <p className="text-xs text-muted-foreground truncate">{i.type === 'rfp' ? 'RFP' : 'Proposal'}{i.sub ? ` · ${i.sub}` : ''}</p>
            </div>
            <Badge variant="outline" className={`text-xs shrink-0 font-mono tabular-nums ${dueTone(i.days_left)}`}>
              {i.days_left === 0 ? 'Today' : `${i.days_left}d`}
            </Badge>
          </button>
        ))
      )}
    </WidgetCard>
  );
}

function IncomingWorkCard({ items, navigate }) {
  return (
    <WidgetCard icon={Inbox} title="Incoming Work" count={items.length} testId="incoming-work-card">
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No recent wins or proposals nearing contract</p>
      ) : (
        items.map(i => (
          <button
            key={`${i.type}-${i.id}`}
            type="button"
            className="w-full text-left flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0 gap-2 hover:bg-secondary/20 transition-colors rounded-sm"
            onClick={() => navigate(i.type === 'lead' ? `/leads?lead=${i.id}` : '/proposals')}
            data-testid={`incoming-item-${i.id}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-normal truncate">{i.title}</p>
              <p className="text-xs text-muted-foreground truncate">
                {i.sub}{i.value ? ` · ${fmtValue(i.value)}` : ''}
              </p>
            </div>
            <Badge variant="outline" className={`text-xs shrink-0 ${i.sub === 'Won Lead' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' : ''}`}>
              {i.sub === 'Won Lead' ? 'Won' : i.sub}
            </Badge>
          </button>
        ))
      )}
    </WidgetCard>
  );
}

export function BizDevRow({ navigate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/dashboard/biz-dev').then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const queue = data.outreach_queue || [];
  const hasQueue = queue.length > 0;

  return (
    <div className={`grid grid-cols-1 ${hasQueue ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4 items-start`} data-testid="bizdev-row">
      {hasQueue && <OutreachQueueCard items={queue} navigate={navigate} />}
      <RfpRadarCard items={data.radar || []} navigate={navigate} />
      <IncomingWorkCard items={data.incoming || []} navigate={navigate} />
    </div>
  );
}
